import React from 'react';

import type { EventType } from '../../../api/types';
import { getResourceCapacity, getResourcesByRoom } from '../../../resources/resources';
import { TavernField } from '../../../ui/components';

export const EVENT_DESCRIPTION_MAX_LENGTH = 2000;

export type EventBasicInfoSectionProps = {
  title: string;
  description: string;
  resourceId: string;
  type: EventType;
  isSubmitting: boolean;
  titleError: string | null;
  hiddenResourceOptionLabel?: string | null;
  unavailableVipResourceIds: string[];
  /** When set (guests), only these tables can be chosen. */
  allowedResourceIds?: string[] | null;
  onTitleChange: (value: string) => void;
  onTitleBlur: () => void;
  onDescriptionChange: (value: string) => void;
  onResourceChange: (value: string) => void;
};

export const EventBasicInfoSection = React.memo(function EventBasicInfoSection({
  title,
  description,
  resourceId,
  type,
  isSubmitting,
  titleError,
  hiddenResourceOptionLabel,
  unavailableVipResourceIds,
  allowedResourceIds,
  onTitleChange,
  onTitleBlur,
  onDescriptionChange,
  onResourceChange,
}: EventBasicInfoSectionProps) {
  const isAllowed = (r: { id: string }) => !allowedResourceIds || allowedResourceIds.includes(r.id);

  return (
    <div className="ttbFormSection">
      <div className="ttbFormSectionHeader">
        <span className="ttbFormSectionIcon">📋</span>
        <h2 className="ttbFormSectionTitle">Основная информация</h2>
      </div>

      <TavernField label="Название" required error={titleError}>
        <input
          type="text"
          placeholder="Введите название"
          value={title}
          disabled={isSubmitting}
          onBlur={onTitleBlur}
          onChange={(e) => onTitleChange(e.target.value)}
          className={['ttbInput', titleError ? 'ttbInputError' : null].filter(Boolean).join(' ')}
        />
      </TavernField>

      <TavernField label="Описание" hint={`${description.length}/${EVENT_DESCRIPTION_MAX_LENGTH}`}>
        <textarea
          placeholder="Необязательно: расскажите о приключении"
          value={description}
          maxLength={EVENT_DESCRIPTION_MAX_LENGTH}
          disabled={isSubmitting}
          onChange={(e) => onDescriptionChange(e.target.value)}
          className="ttbInput ttbInputTextArea"
        />
      </TavernField>

      <TavernField label="Ресурс" required>
        <select
          value={resourceId}
          disabled={isSubmitting}
          onChange={(e) => onResourceChange(e.target.value)}
          className="ttbInput"
        >
          {hiddenResourceOptionLabel ? (
            <option value={resourceId}>
              {hiddenResourceOptionLabel} (на {getResourceCapacity(resourceId, 5)})
            </option>
          ) : null}
          <optgroup label="Левая зона">
            {getResourcesByRoom('left').filter(isAllowed).map((r) => (
              <option key={r.id} value={r.id}>
                {r.label} (на {r.capacity})
              </option>
            ))}
          </optgroup>
          <optgroup label="VIP">
            {getResourcesByRoom('vip').filter(isAllowed).map((r) => (
              <option key={r.id} value={r.id} disabled={unavailableVipResourceIds.includes(r.id)}>
                {r.label} (на {r.capacity}){unavailableVipResourceIds.includes(r.id) ? ' — занято' : ''}
              </option>
            ))}
          </optgroup>
          <optgroup label="Таверна">
            {getResourcesByRoom('tavern').filter(isAllowed).map((r) => (
              <option key={r.id} value={r.id}>
                {r.label} (на {r.capacity})
              </option>
            ))}
          </optgroup>
        </select>
      </TavernField>

      <TavernField label="Тип" required>
        <select value={type} disabled className="ttbInput">
          <option value="boardgames">Настольные игры</option>
          <option value="ping_pong">Настольный теннис</option>
          <option value="fencing">Фехтование</option>
          <option value="archery">Лук</option>
          <option value="hiking">Походы на природу</option>
          <option value="courtyard">Двор (приусадебный участок)</option>
        </select>
      </TavernField>
    </div>
  );
});
